import React, { useMemo } from 'react';
import { useHistory } from 'react-router';
import { track } from '@amplitude/analytics-browser';
import { Box, Icon, Label, Link, Select, Text } from '@nimbus-ds/components';
import { ExclamationCircleIcon } from '@nimbus-ds/icons';
import { useTranslationWithPrefix } from 'commons/utils';
import { fieldHandler } from './FieldHandler.interface';

const MAX_INSTALLMENTS_FALLBACK = 12;

const SETTINGS_INSTALLMENTS_PATH = '/settings/installments';

type InstallmentsFieldProps = fieldHandler & {
  title: string;
  maxInstallments?: number;
  hasMinInstallmentError?: boolean;
};

function InstallmentsField({
  handler,
  value,
  title,
  maxInstallments = MAX_INSTALLMENTS_FALLBACK,
  hasMinInstallmentError = false,
}: InstallmentsFieldProps): JSX.Element {
  const { t } = useTranslationWithPrefix(
    'paymentLink.form.installmentsOptions',
  );
  const history = useHistory();
  const options = useMemo(() => {
    const list: Array<{ value: string; labelKey: string }> = [];
    for (let i = 1; i <= maxInstallments; i++) {
      list.push({ value: String(i), labelKey: `${i}x` });
    }
    return list;
  }, [maxInstallments]);

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    const selected = event.target.value;
    track('nuvempago_payment_link_installments_selected', {
      installments: selected,
    });
    handler(selected);
  };

  return (
    <Box display="grid" gridGap="2">
      <Label htmlFor="payment-link-installments">{title}</Label>
      <Select
        id="payment-link-installments"
        name="installments"
        value={String(value)}
        onChange={handleChange}
        appearance={hasMinInstallmentError ? 'danger' : 'neutral'}
      >
        {options.map((opt) => (
          <Select.Option
            key={opt.labelKey}
            label={t(opt.labelKey)}
            value={opt.value}
          />
        ))}
      </Select>
      {hasMinInstallmentError && (
        <Box display="flex" flexDirection="row" gap="1" alignItems="center">
          <Icon
            color="danger-textLow"
            source={<ExclamationCircleIcon size="small" />}
          />
          <Text color="danger-textLow" fontSize="caption">
            {t('minInstallmentError.prefix')}{' '}
            <Link
              as="a"
              onClick={() => {
                history.push(SETTINGS_INSTALLMENTS_PATH);
              }}
              fontSize="caption"
              textDecoration="underline"
            >
              <Text color="danger-textLow" fontSize="caption">
                {t('minInstallmentError.linkText')}
              </Text>
            </Link>
            {t('minInstallmentError.suffix')}
          </Text>
        </Box>
      )}
    </Box>
  );
}

function InstallmentsFieldSkeleton(): JSX.Element {
  return (
    <Box display="grid" gridGap="2">
      <Text.Skeleton height="14px" width="80px" />
      <Select.Skeleton width="100%" />
    </Box>
  );
}

InstallmentsField.Skeleton = InstallmentsFieldSkeleton;

export default InstallmentsField;
